import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Eyebrow, Row, Screen } from '../components/primitives';
import { coaching, logout } from '../lib/api';
import { tierLabel } from '../lib/pro';
import { useAuthStore } from '../lib/auth-store';
import { qk, useActiveGoals, useEntitlement, useTierLabels, useToday } from '../lib/queries';

/** SC-40. */
export function ProfilePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const today = useToday();
  const goals = useActiveGoals();
  const entitlement = useEntitlement();
  const labels = useTierLabels();
  const score = useQuery({ queryKey: qk.score, queryFn: coaching.score });
  const average = score.data
    ? Math.round(
        score.data.history.reduce((sum, h) => sum + h.score, 0) /
          Math.max(1, score.data.history.length),
      )
    : null;

  return (
    <Screen
      eyebrow="My profile"
      title={today.data?.profile.firstName ?? user?.fullName ?? 'Profile'}
      subtitle={user?.email}
    >
      <section className="grid grid-cols-3 gap-4 border-b border-border px-5 py-6">
        {(
          [
            ['Streak', today.data ? `${today.data.streak.current}d` : '·'],
            ['7-day score', average === null ? '·' : String(average)],
            ['Active goals', goals.data ? String(goals.data.length) : '·'],
          ] as const
        ).map(([label, value]) => (
          <div key={label}>
            <Eyebrow>{label}</Eyebrow>
            <p className="mt-1 font-display text-3xl text-ink">{value}</p>
          </div>
        ))}
      </section>
      {entitlement.data &&
        (entitlement.data.tier === 'free' ? (
          <Row title="Free plan" meta={`See what ${labels.pro} adds`} to="/upgrade" />
        ) : (
          <Row
            title={tierLabel(entitlement.data.tier, labels)}
            meta={
              entitlement.data.endsAt
                ? `Until ${new Date(entitlement.data.endsAt).toLocaleDateString(undefined, { dateStyle: 'long' })}`
                : 'Active'
            }
            to="/upgrade"
          />
        ))}
      {entitlement.data?.credits && (
        <Row
          title="Credits"
          meta={`${entitlement.data.credits.total} to spend${
            entitlement.data.credits.included > 0
              ? `, ${entitlement.data.credits.included} included this month`
              : ''
          }`}
          to="/credits"
        />
      )}
      <Row title="Settings" meta="Birth details, coaching style, password" to="/settings" />
      <Row title="My goals" to="/achieve/goals" />
      <Row title="Invite a friend" meta="Your link and your code" to="/invite" />
      {entitlement.data?.community && (
        <Row
          title={entitlement.data.community.label ?? 'Community'}
          meta="Opens in your browser"
          href={entitlement.data.community.url}
          onClick={() => coaching.event('community_opened')}
        />
      )}
      {/* Internal now. This used to leave for the member subdomain, which is
          both a dead host on some boxes and pointless since the shop, the
          courses and the wallet all live in this app. */}
      <Row title="Store" meta="Products and your orders" to="/shop" />
      <Row title="Courses" meta="What you are enrolled in" to="/learn" />
      <Row title="Wallet" meta="Balance, cash out and top up" to="/wallet" />
      <Row
        title="Log out"
        onClick={() => void logout().then(() => navigate('/welcome', { replace: true }))}
      />
    </Screen>
  );
}
